import { format_datetime_dd_mmm_yy as fmt_datetime } from '../../../utils/format_date_dd_mmm_yy';
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Modal, ModalBody, ModalHeader, Spinner } from 'reactstrap';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';
import ResolverMergeCard, { is_resolver_merge, latest_merge_verdicts } from './ResolverMergeCard';

/**
 * Per-item audit history timeline. Fetches FindingHistory or
 * ActionableHistory rows and renders them in reverse-chronological order.
 *
 * **Read-only ledger.** The per-row Revert button (and its handler) was
 * removed when we cut over to the new chat-driven workflow — restoring a
 * prior snapshot now happens by asking the chat to "set this finding back
 * to ACCEPTED" (or whatever), which goes through the same audit-traced
 * tool path as any other status change. Direct revert was an artifact of
 * the pre-chat era; keeping it would let reps silently mutate state
 * without producing the same kind of explanatory audit trail (note +
 * intent) that chat-driven changes carry.
 *
 * `kind` selects the endpoint family ('findings' | 'actionables').
 *
 * Findings only: resolver merges (`prevState.kind === 'resolver_evidence_added'`)
 * render as a ResolverMergeCard with the ✓ / ✗ verdict chips — the same card
 * as the drawer's History tab. There is no finding state behind this modal
 * to update, so the returned view is dropped and the ledger is refetched.
 */
const HistoryTimeline = ({ open, on_close, store_id, kind, item }) => {
    const [rows, set_rows] = useState([]);
    const [loading, set_loading] = useState(false);
    const [err, set_err] = useState(null);
    const item_id = item ? item.id : null;

    const load = useCallback(async () => {
        if (!open || !item_id) return;
        set_loading(true);
        set_err(null);
        try {
            const resp = await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/${kind}/${item_id}/history`,
            });
            set_rows(Array.isArray(resp) ? resp : []);
        } catch (e) {
            set_err(e.message || String(e));
        } finally {
            set_loading(false);
        }
    }, [open, item_id, store_id, kind]);

    useEffect(() => {
        if (!open || !item_id) {
            set_rows([]);
            set_err(null);
            return;
        }
        load();
    }, [open, item_id, load]);

    if (!open || !item) return null;

    const verdicts = kind === 'findings' ? latest_merge_verdicts(rows) : {};

    return (
        <Modal isOpen={open} toggle={on_close} size='lg' scrollable>
            <ModalHeader toggle={on_close}>
                Audit history
                <span style={{ fontSize: 12, color: '#7f8c8d', fontWeight: 400, marginLeft: 8 }}>
                    {item.title || item.description || item.id}
                </span>
            </ModalHeader>
            <ModalBody>
                {loading && rows.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 24 }}><Spinner size='sm' /></div>
                ) : err ? (
                    <div style={{ color: '#c0392b' }}>{err}</div>
                ) : rows.length === 0 ? (
                    <div style={{ color: '#7f8c8d', fontSize: 13 }}>
                        No history entries yet. This item hasn't been modified since it was first emitted.
                    </div>
                ) : (
                    <div>
                        <div style={{ fontSize: 11, color: '#7f8c8d', marginBottom: 8 }}>
                            Frozen ledger — each entry shows the state immediately BEFORE the change.
                            To restore a prior state, ask the chat to apply the desired status / values
                            (changes go through the same audit-traced tool path).
                        </div>
                        {rows.map((h) => {
                            if (kind === 'findings' && is_resolver_merge(h)) {
                                return (
                                    <ResolverMergeCard
                                        key={h.id}
                                        row={h}
                                        verdict={verdicts[h.id]}
                                        store_id={store_id}
                                        finding_id={item.id}
                                        on_view={null}
                                        reload_history={load}
                                    />
                                );
                            }
                            const prev = h.prevState || {};
                            return (
                                <div
                                    key={h.id}
                                    style={{ padding: '8px 10px', borderLeft: '2px solid #ecf0f1', marginBottom: 6, position: 'relative' }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Badge color={h.changeType === 'reverted' ? 'warning' : 'info'} style={{ fontWeight: 400 }}>
                                            {h.changeType}
                                        </Badge>
                                        <span style={{ fontSize: 12, color: '#7f8c8d' }}>
                                            {fmt_datetime(h.changedAt)}
                                            {h.byUserId ? ` · by ${h.byUserId}` : ' · analyzer'}
                                        </span>
                                    </div>
                                    {h.note ? (
                                        <div style={{ fontSize: 12, color: '#34495e', marginTop: 4, fontStyle: 'italic' }}>
                                            {h.note}
                                        </div>
                                    ) : null}
                                    <div style={{ fontSize: 11, color: '#7f8c8d', marginTop: 4 }}>
                                        prev state: status={String(prev.status)}
                                        {prev.severity ? `, severity=${prev.severity}` : ''}
                                        {prev.repNote ? `, repNote="${String(prev.repNote).slice(0, 80)}…"` : ''}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </ModalBody>
        </Modal>
    );
};

export default HistoryTimeline;
